import { SiSpotify, SiApplemusic, SiYoutubemusic, SiTiktok, SiDeezer, SiTidal, SiSoundcloud, SiPandora, SiIheartradio, SiNapster, SiShazam, SiInstagram, SiFacebook, SiTwitch, SiSnapchat, SiPinterest, SiBandcamp, SiAudiomack, SiBeatport, SiMixcloud, SiNeteasecloudmusic, SiLine, SiVk, SiRoblox, SiYoutube, SiWechat, SiDiscord, SiReddit, SiTelegram, SiWhatsapp, SiThreads, SiKuaishou, SiRumble, SiX, SiVimeo } from "react-icons/si";
import { ShoppingBag } from "lucide-react";
import { FadeUp, RevealLine } from "./AnimatedSection";
import { useLanguage } from "@/context/LanguageContext";

const ICONS = [SiSpotify, SiApplemusic, SiYoutubemusic, SiTiktok, ShoppingBag, SiDeezer];
const BRAND_COLORS = ["#1ED760", "#FA243C", "#FF0000", "#25F4EE", "#FF9900", "#A238FF"];

const MORE_PLATFORMS = [
  { name: "Tidal", icon: SiTidal, color: "#00FFFF" },
  { name: "SoundCloud", icon: SiSoundcloud, color: "#FF5500" },
  { name: "Pandora", icon: SiPandora, color: "#00A9E0" },
  { name: "iHeartRadio", icon: SiIheartradio, color: "#C6002B" },
  { name: "Napster", icon: SiNapster, color: "#FF0000" },
  { name: "Shazam", icon: SiShazam, color: "#0088FF" },
  { name: "Instagram", icon: SiInstagram, color: "#E4405F" },
  { name: "Facebook", icon: SiFacebook, color: "#1877F2" },
  { name: "Twitch", icon: SiTwitch, color: "#9146FF" },
  { name: "Snapchat", icon: SiSnapchat, color: "#FFFC00" },
  { name: "Pinterest", icon: SiPinterest, color: "#BD081C" },
  { name: "Bandcamp", icon: SiBandcamp, color: "#408294" },
  { name: "Audiomack", icon: SiAudiomack, color: "#FFA200" },
  { name: "Beatport", icon: SiBeatport, color: "#01FF95" },
  { name: "Mixcloud", icon: SiMixcloud, color: "#5000FF" },
  { name: "NetEase Cloud Music", icon: SiNeteasecloudmusic, color: "#C20C0C" },
  { name: "LINE Music", icon: SiLine, color: "#00C300" },
  { name: "VKontakte", icon: SiVk, color: "#0077FF" },
  { name: "Roblox", icon: SiRoblox, color: "#FFFFFF" },
  { name: "YouTube", icon: SiYoutube, color: "#FF0000" },
  { name: "WeChat", icon: SiWechat, color: "#07C160" },
  { name: "Discord", icon: SiDiscord, color: "#5865F2" },
  { name: "Reddit", icon: SiReddit, color: "#FF4500" },
  { name: "Telegram", icon: SiTelegram, color: "#26A5E4" },
  { name: "WhatsApp", icon: SiWhatsapp, color: "#25D366" },
  { name: "Threads", icon: SiThreads, color: "#FFFFFF" },
  { name: "Kuaishou", icon: SiKuaishou, color: "#FF5000" },
  { name: "Rumble", icon: SiRumble, color: "#85C742" },
  { name: "X", icon: SiX, color: "#FFFFFF" },
  { name: "Vimeo", icon: SiVimeo, color: "#1AB7EA" },
];

const stripAccents = (str) => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export const PlatformsCatalog = () => {
  const { t } = useLanguage();

  return (
    <section data-testid="platforms-catalog-section" className="relative py-28 md:py-36 bg-[#0C0C0C] border-y border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16 md:mb-20 max-w-2xl">
          <p className="text-brand-red text-xs font-mono tracking-[0.25em] font-semibold uppercase mb-4">{t.platformsCatalog.label}</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight">
            <RevealLine text={t.platformsCatalog.title} />
          </h2>
        </div>

        <div className="grid grid-cols-1 border-t border-white/[0.08]">
          {t.platformsCatalog.items.map((item, i) => {
            const Icon = ICONS[i];
            const color = BRAND_COLORS[i];
            return (
              <FadeUp
                key={item.name}
                delay={i * 0.05}
                data-testid={`platform-catalog-item-${i}`}
                className="group grid grid-cols-1 md:grid-cols-12 items-center gap-4 md:gap-6 py-7 border-b border-white/[0.08] hover:bg-white/[0.02] transition-colors duration-300 px-2"
              >
                <div className="md:col-span-1 flex items-center">
                  <span className="font-heading font-black text-neutral-700 text-2xl md:text-3xl tracking-tight group-hover:text-neutral-500 transition-colors duration-300">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="md:col-span-3 flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl border border-white/10 bg-white/[0.03] flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300"
                    style={{ "--brand-color": color }}
                  >
                    <Icon className="w-4 h-4 text-[color:var(--brand-color)]" />
                  </div>
                  <span className="font-heading font-bold text-white text-lg">{item.name}</span>
                </div>
                <p className="md:col-span-6 text-neutral-400 text-sm leading-relaxed">{item.desc}</p>
                <div className="md:col-span-2 flex md:justify-end">
                  <span className="text-[11px] font-semibold text-neutral-300 bg-white/[0.05] border border-white/10 rounded-full px-3 py-1.5 whitespace-nowrap">
                    {item.tag}
                  </span>
                </div>
              </FadeUp>
            );
          })}
        </div>

        <div className="mt-16 md:mt-20">
          <p className="text-neutral-500 text-xs font-mono tracking-[0.25em] font-semibold uppercase mb-6">{t.platformsCatalog.moreLabel}</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 md:gap-4">
            {MORE_PLATFORMS.map((p, i) => {
              const Icon = p.icon;
              return (
                <FadeUp
                  key={p.name}
                  delay={i * 0.03}
                  data-testid={`platform-logo-tile-${stripAccents(p.name).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  style={{ "--brand-color": p.color }}
                  className="group flex flex-col items-center justify-center gap-2.5 py-6 px-3 rounded-2xl border border-white/[0.08] bg-white/[0.02] hover:-translate-y-1.5 hover:border-[color:var(--brand-color)]/40 hover:bg-white/[0.04] hover:shadow-[0_12px_30px_-10px_var(--brand-color)] transition-all duration-300"
                >
                  <Icon className="w-6 h-6 text-neutral-500 group-hover:text-[color:var(--brand-color)] group-hover:scale-110 transition-all duration-300" />
                  <span className="text-xs font-medium text-neutral-400 group-hover:text-white transition-colors duration-300 text-center leading-tight">{p.name}</span>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
